import React, { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { withWindow } from '@shared/utils/withWindow';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { PointsOnMapFragment, useIncrementInterestedGuestCountMutation } from '@graphql/generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { Flex, ButtonV2 } from '@withjoy/joykit';
import { useLayout } from '@apps/guest/packages/layout-engine/layouts/LayoutProvider';
import { useGuestSiteState } from '@apps/guest/routes/GuestSite/GuestSite.state.provider';
import { useHotelBookingRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { useCustomPageTelemetry } from '../../../../Custom.telemetry';
import {
  AccommodationTileDescription,
  AccommodationTileDiscount,
  AccommodationTileSchedule,
  AccommodationTileImage,
  AccommodationTileSubtitle,
  AccommodationTileTitle,
  AccommodationTileAddress,
  AccommodationTilePhone,
  AccommodationTileEyebrow
} from './components';
import { AccommodationCombined, AccommodationCombinedType } from './Accommodation.types';
import { useFeatureValue } from '@shared/core/featureFlags';
import queryString from 'query-string';
import { config } from '@static/js/env.config';

interface AccommodationsTileProps extends AccommodationCombined {
  locationInfo?: PointsOnMapFragment;
  eventPhoto: string;
  eventHandle: string;
  isFirst: boolean;
  isLast: boolean;
  showDefaultData?: boolean;
  setLocationBeingHovered?: (id: string | undefined) => void;
}

const AccommodationsTile: React.FC<AccommodationsTileProps> = props => {
  const { layout } = useLayout();
  const {
    description,
    displayName,
    photo,
    eventPhoto,
    starRating,
    isFavorite,
    cutoffDate,
    strikeoutPricePerNight,
    pricePerNight,
    checkInDate,
    checkOutDate,
    phone,
    id,
    url,
    address,
    isFirst,
    joyPlaceId,
    zhId,
    isLast,
    eventHandle,
    type,
    showDefaultData,
    setLocationBeingHovered,
    interestedGuestCount
  } = props;
  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');
  const { roomblock, buildPath } = useHotelBookingRoutePaths();
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const isMobileOrTablet = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'md' }));
  const { setShowNoLeadDialog, enabledAdminGuestSiteBannerDialog, hotelTileGuestCountEnabled } = useGuestSiteState();
  const joyPlaceFallbackEnabled = useFeatureValue('joyPlaceFallback').value === 'on';
  const [incrementInterestedGuestCount] = useIncrementInterestedGuestCountMutation();

  const { ctaTile } = useMemo(() => {
    const buttonCta = {
      [AccommodationCombinedType.Custom]: accTrans.selectCTACustom(),
      [AccommodationCombinedType.Hotel]: accTrans.selectCTAHotel(),
      [AccommodationCombinedType.Roomblock]: accTrans.selectCTARoomBlock()
    };
    return {
      ctaTile: buttonCta[type]
    };
  }, [type, accTrans]);
  const telemetry = useCustomPageTelemetry();
  const telemetryHotelInfo = {
    hotelName: displayName || '',
    isHotelPreferred: isFavorite || false,
    hotelPrice: pricePerNight || undefined,
    hotelStrikeThroughPrice: strikeoutPricePerNight || undefined
  };

  const isBrannan = layout === 'brannan';

  const handleOnSelectAccommodation = useEventCallback(() => {
    if (type === AccommodationCombinedType.Roomblock) {
      handleOnSelectRoomBlock();
      return;
    }
    telemetry.selectAccommodationClicked('accommodations', telemetryHotelInfo, type);
    if (joyPlaceFallbackEnabled && joyPlaceId) {
      withWindow(global => global.open(`${global.location.origin}/${eventHandle}/joyplace/${joyPlaceId}`, '_blank'));
    } else if (url) {
      // Every link should pass through redirect service, some accommodation places have hotel link instead of eventredirect link
      const redirectServicePath = `${window.location.origin}/eventredirect/`;
      const redirectServiceLink = queryString.stringifyUrl({ url: `${config.redirectServiceUri}/redirect`, query: { url } });
      withWindow(global => global.open(`${url.includes(redirectServicePath) ? url : redirectServiceLink}`, '_blank'));
    }
  });

  const handleOnSelectRoomBlock = useEventCallback(() => {
    if (enabledAdminGuestSiteBannerDialog) {
      telemetry.leadGenDialogPromptedClicked();
      setShowNoLeadDialog(true);
    } else {
      if (hotelTileGuestCountEnabled) {
        incrementInterestedGuestCount({
          variables: { reservedRoomBlockId: id }
        });
      }

      const telemetryHotelInfoWithInterestCountIfAvailable = {
        ...telemetryHotelInfo,
        displayedInterestedGuestCount: hotelTileGuestCountEnabled ? interestedGuestCount || 0 : undefined
      };

      telemetry.selectRoomClicked('accommodations', telemetryHotelInfoWithInterestCountIfAvailable);

      withWindow(global => global.open(`${global.location.origin}${buildPath(eventHandle, roomblock, id)}`, '_blank'));
    }
  });

  return (
    <Flex paddingTop={isBrannan ? { _: 8, sm2: 0 } : isFirst ? 0 : 8} paddingBottom={isBrannan && isLast ? (isMobile ? 8 : 0) : 8} width={'100%'} justifyContent="center">
      <Flex maxWidth={isBrannan ? 520 : 500} width={'100%'}>
        <Flex width={'100%'} display={'column'}>
          <AccommodationTileImage
            id={id}
            type={type}
            telemetryHotelInfo={telemetryHotelInfo}
            eventHandle={eventHandle}
            photoUrl={photo?.url}
            showDefaultData={showDefaultData}
            hotelTileGuestCountEnabled={hotelTileGuestCountEnabled}
            interestedGuestCount={interestedGuestCount}
          />
          <Flex flexDirection={'column'} width={'100%'} paddingTop={6} paddingBottom={7}>
            {hotelTileGuestCountEnabled && type === AccommodationCombinedType.Roomblock && <AccommodationTileEyebrow displayName={accTrans.roomBlockEyebrow()} />}
            <AccommodationTileTitle displayName={displayName} />
            <AccommodationTileSubtitle
              starRating={starRating}
              venueLatitude={props.locationInfo?.latitude}
              venueLongitude={props.locationInfo?.longitude}
              accommodationLatitude={props.latitude}
              accommodationLongitude={props.longitude}
            />
          </Flex>
          <Flex flexDirection={'column'} rowGap={6}>
            <AccommodationTileDescription eventPhoto={eventPhoto} description={description} isFavorite={!!isFavorite} />
            <AccommodationTileSchedule type={type} cutoffDate={cutoffDate} />
            <AccommodationTileAddress type={type} address={address} />
            <AccommodationTilePhone type={type} phone={phone} />
            <AccommodationTileDiscount
              type={type}
              checkInDate={checkInDate}
              checkOutDate={checkOutDate}
              pricePerNight={pricePerNight}
              strikeoutPricePerNight={strikeoutPricePerNight}
            />

            {type === AccommodationCombinedType.Roomblock || url || (joyPlaceFallbackEnabled && joyPlaceId && zhId) ? (
              <ButtonV2
                width="100%"
                intent="neutral"
                shape="rounded"
                fontWeight={600}
                fontSize={16}
                fontFamily={'Inter UI'}
                onMouseEnter={() => !isMobileOrTablet && setLocationBeingHovered?.(id)}
                onMouseLeave={() => !isMobileOrTablet && setLocationBeingHovered?.(undefined)}
                onClick={handleOnSelectAccommodation}
              >
                {ctaTile}
              </ButtonV2>
            ) : null}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

AccommodationsTile.displayName = 'AccommodationsTile';

export { AccommodationsTile };
